import React from 'react'
import { Box, Typography, LinearProgress } from '@mui/material'
import CloudDownloadIcon from '@mui/icons-material/CloudDownload'
import VerifiedIcon from '@mui/icons-material/Verified'
import PlayCircleIcon from '@mui/icons-material/PlayCircle'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import ErrorIcon from '@mui/icons-material/Error'
import { UpdateProgress } from '../../ota/types'

interface OTAProgressProps {
  progress: UpdateProgress
}

function getStageIcon(stage: string) {
  switch (stage) {
    case 'downloading':
      return <CloudDownloadIcon sx={{ color: '#2196f3', mr: 1 }} />
    case 'verifying':
      return <VerifiedIcon sx={{ color: '#ff9800', mr: 1 }} />
    case 'activating':
      return <PlayCircleIcon sx={{ color: '#9c27b0', mr: 1 }} />
    case 'complete':
      return <CheckCircleIcon sx={{ color: '#4caf50', mr: 1 }} />
    case 'error':
      return <ErrorIcon sx={{ color: '#f44336', mr: 1 }} />
    default:
      return <CloudDownloadIcon sx={{ color: '#2196f3', mr: 1 }} />
  }
}

function getStageColor(stage: string) {
  switch (stage) {
    case 'downloading':
      return '#2196f3'
    case 'verifying':
      return '#ff9800'
    case 'activating':
      return '#9c27b0'
    case 'complete':
      return '#4caf50'
    case 'error':
      return '#f44336'
    default:
      return '#2196f3'
  }
}

const OTAProgress: React.FC<OTAProgressProps> = ({ progress }) => {
  return (
    <Box 
      sx={{ 
        width: '100%',
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        borderRadius: 2,
        p: 3,
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
        border: '1px solid #e0e0e0',
      }}
    >
      <Box 
        display='flex' 
        alignItems='center' 
        justifyContent='center'
        mb={2}
      >
        {getStageIcon(progress.stage)}
        <Typography 
          variant='h6' 
          color={progress.stage === 'error' ? 'error' : 'textPrimary'}
          fontWeight='medium'
        >
          {progress.stage === 'error' ? 'Update Failed' : 'App Update in Progress'}
        </Typography>
      </Box>
      
      <Box mb={2}>
        <LinearProgress 
          variant='determinate' 
          value={progress.progress} 
          sx={{
            height: 8,
            borderRadius: 4,
            backgroundColor: 'rgba(0, 0, 0, 0.1)',
            '& .MuiLinearProgress-bar': {
              backgroundColor: getStageColor(progress.stage),
              borderRadius: 4,
            },
          }}
        />
      </Box>
      
      <Box textAlign='center'>
        <Typography 
          variant='body2' 
          color='textSecondary'
          fontWeight='medium'
        >
          {progress.message}
        </Typography>
        <Typography 
          variant='caption' 
          color='textSecondary'
          display='block'
          mt={0.5}
        >
          {Math.round(progress.progress)}% complete
        </Typography>
      </Box>
      
      {progress.stage === 'error' && progress.error && (
        <Box mt={2} textAlign='center'>
          <Typography 
            variant='caption' 
            color='error'
            fontWeight='bold'
          >
            {progress.error}
          </Typography>
        </Box>
      )}
      
      {progress.stage !== 'complete' && progress.stage !== 'error' && (
        <Box mt={2} textAlign='center'>
          <Typography 
            variant='caption' 
            color='primary'
            fontWeight='bold'
          >
            Please wait, do not close the app
          </Typography>
        </Box>
      )}
    </Box>
  )
}

export default OTAProgress